/**
 * Remembers the author's last Markdown chapter-split choice.
 *
 * An author importing a manuscript as one `.md` file per chapter picks the same
 * split mode every single time (57 files = 57 trips through the selector), so
 * the choice is persisted in localStorage and pre-fills the next import. The
 * stored value drives the FIRST conversion too, not just the selector — see
 * `chapterFileUpload.js`, which reads it before calling `markdownify`.
 *
 * Scope is deliberately per-browser, not per-book: it is a workflow habit
 * ("my files are one chapter each"), not a property of any one manuscript.
 *
 * Mirrors `SPLIT_MODES` in api/src/utils/markdown/markdownImport.js. Anything
 * else — an unknown mode, a value from a future/older build, a corrupted
 * entry — degrades to `auto`, so a stale entry can never make the client post
 * a `splitOn` the route would reject with 400 `invalid_split_mode`.
 */
export const SPLIT_MODES = ['auto', 'h1', 'h2', 'h3', 'none'];

export const DEFAULT_SPLIT_MODE = 'auto';

const STORAGE_KEY = 'br-md-import-split-mode';

/**
 * The last split mode the author used, or `auto` when they never chose one.
 *
 * Storage access is guarded: `localStorage` throws on access in Safari private
 * mode and in sandboxed iframes, and it is absent in non-browser environments.
 * A failure here must never break the importer, so it falls back to the default.
 */
export function readSplitModePreference() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return SPLIT_MODES.includes(stored) ? stored : DEFAULT_SPLIT_MODE;
  } catch (e) {
    return DEFAULT_SPLIT_MODE;
  }
}

/**
 * Remember a split mode for the next import. Unknown modes are ignored rather
 * than stored, so `readSplitModePreference` can trust what it reads back.
 */
export function writeSplitModePreference(mode) {
  if (!SPLIT_MODES.includes(mode)) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch (e) {
    // Storage full or unavailable — the choice just won't be remembered.
  }
}

export default {
  SPLIT_MODES,
  DEFAULT_SPLIT_MODE,
  readSplitModePreference,
  writeSplitModePreference
};
